import type { FontFamily, TextAlignment } from 'domains/text_nodes';
import type { Dimension, LoadingStatus, PickRequired } from 'types/common_types';

export type CartProductsSliceInitialState = {
  status: LoadingStatus;
  productsPerPage: number;
  page: number;
};

type CartProductParams = {
  product_api_id?: number;
  quantity?: number;
  canvas_rotation?: number;
  /** 8 character Hex value */
  border_color?: string;
  addon?: boolean;
  custom_size?: Dimension;
};

type CartProductCreateParams = PickRequired<CartProductParams, 'product_api_id' | 'quantity'>;

type CartProductOptionParams = {
  option_api_id?: number;
  quantity?: number;
};

type CartProductOptionCreateParams = Required<CartProductOptionParams>;

export type CartProductPhotoStyleFilter = 'black_and_white' | 'sepia_a' | 'sepia_b';

export type CartProductPhotoParams = {
  node_id?: number;
  url?: string;
  filename?: string;
  external_asset_id?: number;
  rotation?: number;
  style_filter?: CartProductPhotoStyleFilter;
  crop?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  _destroy?: boolean;
};

type RequiredPhotoParams =
  | 'node_id'
  | 'url'
  | 'filename'
  | 'external_asset_id'
  | 'rotation'
  | 'crop';
export type CartProductPhotoCreateParams = PickRequired<
  CartProductPhotoParams,
  RequiredPhotoParams
>;

type CartProductTextNodeParams = {
  node_id?: number;
  font_size?: string;
  font_family?: string;
  text_color?: string;
  text_horizontal_alignment?: string;
  text_vertical_alignment?: string;
  text_content?: string;
  ppi?: number;
  auto_fit?: boolean;
  svg?: string;
};

export type CartProductTextNodeCreateParams = Required<CartProductTextNodeParams>;

export type CartProductForCreate = CartProductCreateParams & {
  cart_product_options_attributes?: CartProductOptionCreateParams[];
  cart_photos_attributes?: CartProductPhotoCreateParams[];
  cart_text_nodes_attributes?: CartProductTextNodeCreateParams[];
};

export type CartProductForUpdate = CartProductParams & {
  id: number;
  cart_product_options_params?: CartProductOptionParams & { id?: number }[];
  cart_photos_attributes?: CartProductPhotoParams & { id?: number }[];
  cart_text_nodes_attributes?: CartProductTextNodeParams & { id?: number }[];
};

export type CartProduct = {
  id: number;
  name?: string;
  quantity?: number;
  /** Thumbnail preview image */
  thumbnail_url?: string | null;
  /** Full size preview image */
  preview_url?: string | null;
  product_price?: number;
  discount_amount?: number;
  discount_percentage?: number;
  canvas_rotation?: number;
  border_color?: string | null;
  addon?: boolean;
  product_api_id?: number;
  created_at?: string;
  updated_at?: string;
  custom_size?: { width: string | null; height: string | null };
  unsupported_order_option_types?: string[];
  photos?: CartProductPhoto[];
  options?: CartProductOption[];
  text_nodes?: CartProductTextNode[];
  pricing?: Required<CartItemPricing>;
};

export type CartProductMaybeThumb = Omit<Required<CartProduct>, 'thumbnail_url' | 'preview_url'> & {
  thumbnail_url?: string | null;
  preview_url?: string | null;
};

export type ExpandedCartProduct = CartProductMaybeThumb & {
  catalog_api_id: number;
  sub_catalog_api_id: number;
};

export type CartProductPhoto = {
  id: number;
  url: string;
  filename: string;
  external_asset_id: string;
  node_id: number;
  created_at: string;
  updated_at: string;
  style_filter: CartProductPhotoStyleFilter;
  crop: {
    rotation: number;
    x: string;
    y: string;
    w: string;
    h: string;
  };
};

export type CartProductOption = {
  id: number;
  name: string;
  quantity: number;
  hidden: boolean;
  option_api_id: number;
  created_at: string;
  updated_at: string;
  pricing: CartItemPricing;
};

export type CartItemPricing = {
  /** Base price of item */
  unit_price_before_options?: number;
  /** Base price of item, including options */
  unit_price: number;
  /** Base price minus discounts */
  unit_price_after_discount: number;
  /** Amount of discount */
  discount: number;
  /** Price with quantity applied, no discounts applied */
  subtotal: number;
  /** Price with quantity and discounts applied */
  total: number;
};

// TODO: change things in here to the values on text node data types
export type CartProductTextNode = {
  id: number;
  node_id: number;
  font_size: string;
  font_family: FontFamily;
  text_color: string;
  text_horizontal_alignment: TextAlignment;
  text_vertical_alignment: TextAlignment;
  text_content: string;
  auto_fit: boolean;
  svg: string;
  ppi: number;
  rendered_asset_url: string;
  rendered_asset_url_updated_at: string;
  created_at: string;
  updated_at: string;
};
